import { AlertCircle, AlertTriangle, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { failedLandscapeReason } from "@/utils/landscapeRunStatus";

export interface LandscapeStateRowProps {
  kind: "failed" | "duplicate" | "creating";
  phrase: string;
  statusInfo?: string | null;
  readOnly?: boolean;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
  onRetry?: () => void;
  retryDisabled?: boolean;
}

const NO_INFO = "No additional info available.";

/**
 * The failed, duplicate and creating rows of the Landscapes table
 * (`design-input/add-landscapes-row-states/sources.md § The table`). One
 * tree for all three; a presentational row that calls no data hook.
 */
export function LandscapeStateRow({
  kind,
  phrase,
  statusInfo,
  readOnly = false,
  onDismiss,
  dismissDisabled,
  onRetry,
  retryDisabled,
}: LandscapeStateRowProps) {
  const colour =
    kind === "failed"
      ? "text-status-error-strong"
      : kind === "duplicate"
        ? "text-status-warning-strong"
        : "text-status-success-strong";

  return (
    <TableRow className="animate-fade-in hover:bg-transparent">
      {!readOnly && <TableCell className="px-3 py-2 w-10" />}
      <TableCell className="px-3 py-2 w-8" />
      <TableCell colSpan={5} className="px-3 py-2 text-xs">
        {/* An auto-layout table sizes this colSpan cell from its content's min-content: `w-0` removes the
            phrase's contribution and `min-w-full` fills the cell, else a nowrap phrase widens the table (AUT-618). */}
        <div
          className={cn("flex w-0 min-w-full items-center gap-3", kind === "creating" && "text-muted-foreground")}
          aria-disabled={kind === "creating" ? true : undefined}
        >
          <TruncatedText className="min-w-0 text-muted-foreground">{phrase}</TruncatedText>
          <span className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap", colour)}>
            {kind === "failed" && (
              <>
                <AlertCircle className="h-3.5 w-3.5" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>Couldn't create landscape.</span>
                  </TooltipTrigger>
                  <TooltipContent>{failedLandscapeReason(statusInfo) ?? NO_INFO}</TooltipContent>
                </Tooltip>
                {!readOnly && (
                  <button
                    type="button"
                    className="underline underline-offset-2"
                    onClick={onRetry}
                    disabled={retryDisabled}
                  >
                    Retry
                  </button>
                )}
              </>
            )}
            {kind === "duplicate" && (
              <>
                <AlertTriangle className="h-3.5 w-3.5" />
                Duplicate. Instead, refresh the existing landscape below.
              </>
            )}
            {kind === "creating" && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Creating landscape
              </>
            )}
          </span>
        </div>
      </TableCell>
      <TableCell className="px-3 py-2 text-right">
        {!readOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Dismiss"
            onClick={onDismiss}
            disabled={dismissDisabled}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
